import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { setWsEventHandler } from './socket';
import { applyEvent, isWsEvent, type IOperatorNotificationV1, type WsEvent } from './apply-event';
import { toNotification } from './notification-feed';
import { useNotificationsStore } from '@/state/notifications.store';
import { receiveAlert } from '@/lib/alerts/alerts.store';
import { useOperator } from '@/auth/use-operator';
import type { OperatorMe } from '@/lib/me/api';

// Bridges the module-level Socket.IO event dispatch (src/lib/ws/socket.ts) into
// the central applyEvent merge (NEX-149) AND the notification center (NEX-296 /
// NEX-299 item 5). Must be mounted inside the QueryClientProvider so
// useQueryClient() resolves.
//
// Two consumers of the SAME stream:
//   1. applyEvent  → TanStack Query cache merge (live data, no refetch).
//   2. toNotification → the header-bell feed (store) + a toast for meaningful ones.
// Pattern mirrors AuthBridge — a null-render component wiring a module-level
// handler at mount, unwiring at unmount.
//
// NEX-296 / S22U — GLOBAL run notifications. The bridge is mounted at the app
// root, above the router, so it is the only place in the app that is listening on
// every screen. That is what makes `operator-bff:operator-notification@v1` work:
// the operator fires a bulk run, walks away to another disruption / the flights
// monitor / their profile, and the result still finds them. Everything below is
// push-driven — there is no timer anywhere in this file, and there must never be
// one: a toast that polls is a toast that lies about when the run finished.

/** Cap on the render-once memory. See `seenNotificationIds` below. */
const MAX_SEEN_NOTIFICATION_IDS = 50;

/**
 * Surface `operator-bff:operator-notification@v1`: the durable bell entry.
 * Issue #735: floating toasts removed; the bell keeps all notifications visible.
 */
function surfaceOperatorNotification(notification: IOperatorNotificationV1): void {
  // v1 discriminator. A `kind` this build doesn't know is not an error and not a
  // malformed event — it's a newer BFF. Ignore it rather than guessing a shape.
  if (notification.kind !== 'run') return;

  const { run, severity } = notification;

  // The bell keeps the record (click-through by disruptionUrn).
  // No toast — the bell is the single notification surface.
  useNotificationsStore.getState().push({
    title: notification.title,
    description: notification.message,
    tone: severity,
    toast: false,
    disruptionUrn: run.disruptionUrn,
  });
}

export function WsEventBridge(): null {
  const qc = useQueryClient();
  const { isAuthenticated } = useAuth0();
  // NEX-296 render-once memory: the last N notification ids surfaced, oldest first.
  // The BFF's `id` is `<runId>:<status>`, and it is needed because delivery is not
  // exactly-once — several BFF replicas can each observe the same terminal
  // transition, and a socket reconnect re-runs the operator-room join, so the same
  // envelope can land twice. Bounded, because a long shift must not grow it forever.
  const seenNotificationIds = useRef<string[]>([]);
  // Warm ['auth','me'] so the handler below can attribute self-locks. Gated on
  // isAuthenticated because this bridge is mounted before login (app.tsx) — an
  // ungated /me here goes out tokenless → 401 → force-logout → redirect loop
  // (the bug). With the gate it stays dormant until the session exists. The
  // handler reads the cache via getQueryData at event time, so it always sees
  // the latest value without needing `me` in the effect deps.
  useOperator({ enabled: isAuthenticated });

  useEffect(() => {
    setWsEventHandler((raw) => {
      if (!isWsEvent(raw)) {
        console.debug('[ws] discarding malformed event', raw);
        return;
      }
      // Re-widen to the full union before discriminating on `type`. The socket
      // hands us `{ type: string; [k: string]: unknown }`, and the one union
      // member carrying an index signature (`flight.status-changed`) is the only
      // one assignable to that — so TS narrows the guard down to just it and every
      // other `type` comparison reads as "no overlap". The guard has already
      // validated the shape at runtime; this restores the type it proved.
      const event = raw as WsEvent;

      if (event.type === 'OperatorNotification') {
        // Render-once gate, ahead of EVERYTHING — a duplicate must not re-push
        // to the bell and must not re-invalidate either. Survives a socket drop
        // because this ref lives with the component, which outlives every
        // reconnect (the handler is registered once, on mount, not per connection).
        const seen = seenNotificationIds.current;
        if (seen.includes(event.payload.id)) return;
        seen.push(event.payload.id);
        if (seen.length > MAX_SEEN_NOTIFICATION_IDS) {
          seen.splice(0, seen.length - MAX_SEEN_NOTIFICATION_IDS);
        }
      }

      // Data merge first so a click-through from the bell lands on fresh data.
      applyEvent(qc, event);

      // Operator alerts (operator-notifications U1): the alert host's policy decides
      // how to say it — quiet while operating, a dialog elsewhere, a summary on return.
      // Dedupe on the alert `id` lives in the alerts store (bounded), not here.
      if (event.type === 'OperatorAlert') {
        receiveAlert(event.payload);
        return;
      }

      if (event.type === 'OperatorNotification') {
        surfaceOperatorNotification(event.payload);
        return;
      }

      // The operator-facing feed. The store stamps id/at + bumps the unread badge.
      // Issue #735: no floating toasts — the bell is the single notification surface.
      // Pass the current operator (from the cached /v1/me) so self-locks / self-actions
      // are suppressed — your own write must not notify "locked by another operator".
      const meUrn = qc.getQueryData<OperatorMe>(['auth', 'me'])?.operatorUrn;
      const draft = toNotification(event, meUrn);
      if (!draft) return;
      useNotificationsStore.getState().push(draft);
    });
    return () => setWsEventHandler(null);
  }, [qc]);

  return null;
}

